import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { B2BCompany, Price, Product } from 'npx-family-happy-common';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs';
import { ProductSelectComponent } from 'src/app/shared/components/custom-select/product-select/product-select.component';
import { B2BCompanyCatalogService } from '../b2b-company-catalog.service';
import { AREAS, CatalogFHService, PROVINCES, errorMessage, euro, lineOf, provinceLabel } from '../catalog.model';

type Mode = 'create' | 'link';

/**
 * Crea i prezzi di un prodotto (uno per provincia, come la pagina Prezzi), modifica o elimina un prezzo,
 * o collega al prodotto un prezzo esistente.
 */
@Component({
  selector: 'app-catalog-price-modal',
  templateUrl: './catalog-price-modal.component.html',
  styleUrls: ['./catalog-price-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ProductSelectComponent],
})
export class CatalogPriceModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) company!: B2BCompany;
  @Input({ required: true }) service!: CatalogFHService;
  @Input({ required: true }) product!: Product;
  /** Valorizzato = modifica. */
  @Input() price: Price | null = null;
  @Output() saved = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  readonly provinces = PROVINCES;
  readonly lineOf = lineOf;
  readonly euro = euro;
  readonly provinceLabel = provinceLabel;

  mode: Mode = 'create';
  saving = false;
  formInvalid = false;
  confirmDelete = false;
  errorText = '';

  /** Importo in euro nel form, in centesimi verso il backend. */
  form = new UntypedFormGroup({
    amount: new UntypedFormControl(null, [Validators.required, Validators.min(0)]),
    stripeID: new UntypedFormControl(''),
    province: new UntypedFormControl('VR', [Validators.required]),
  });
  /** Province scelte per un nuovo prezzo. */
  selectedProvinces: string[] = [];

  /** Filtri come nella pagina Prezzi: prodotto (typeahead), Stripe ID, provincia. */
  private searchProductID = new BehaviorSubject<number | null>(null);
  searchStripeID = new FormControl('', { nonNullable: true });
  searchProvince = new FormControl<string | null>(null);
  results: Price[] = [];
  searching = false;
  selected: Price | null = null;

  private unsubscribe = new Subject<void>();

  constructor(private catalog: B2BCompanyCatalogService) {}

  get isEdit() {
    return !!this.price;
  }

  get tone() {
    return AREAS[lineOf(this.service.businessLine).area].tone;
  }

  get allProvinces() {
    return this.selectedProvinces.length === this.provinces.length;
  }

  ngOnInit(): void {
    if (this.price) {
      this.form.patchValue({
        amount: this.price.amount / 100,
        stripeID: this.price.stripeID ?? '',
        province: this.price.province,
      });
    }
    combineLatest([
      this.searchProductID,
      this.searchStripeID.valueChanges.pipe(startWith(''), debounceTime(250), distinctUntilChanged()),
      this.searchProvince.valueChanges.pipe(startWith(null)),
    ])
      .pipe(
        switchMap(([productID, stripeID, province]) => {
          this.searching = true;
          return this.catalog.searchPrices({ productID, stripeID: stripeID.trim(), province: province || null });
        }),
        takeUntil(this.unsubscribe)
      )
      .subscribe({
        next: (prices) => {
          this.results = prices;
          this.searching = false;
        },
        error: () => (this.searching = false),
      });
  }

  setMode(mode: Mode) {
    this.mode = mode;
    this.errorText = '';
  }

  /** Dal typeahead dei prodotti; `undefined` quando la selezione viene azzerata. */
  filterByProduct(product: Product | undefined) {
    this.selected = null;
    this.searchProductID.next(product?.id ?? null);
  }

  toggleProvince(value: string) {
    this.selectedProvinces = this.selectedProvinces.includes(value)
      ? this.selectedProvinces.filter((p) => p !== value)
      : [...this.selectedProvinces, value];
  }

  toggleAllProvinces() {
    this.selectedProvinces = this.allProvinces ? [] : this.provinces.map((p) => p.value);
  }

  inThisProduct(price: Price) {
    return price.product?.id === this.product.id;
  }

  submit() {
    this.errorText = '';
    if (this.mode === 'link' && !this.isEdit) {
      if (!this.selected) {
        this.errorText = 'Seleziona il prezzo da collegare.';
        return;
      }
      this.run(this.catalog.updatePrice(this.selected, this.product, this.service, this.company), 'Prezzo collegato');
      return;
    }
    const provincesMissing = !this.isEdit && !this.selectedProvinces.length;
    if (this.form.invalid || provincesMissing) {
      this.formInvalid = true;
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.value;
    const amount = Math.round(+value.amount * 100);
    const stripeID = value.stripeID?.trim() ?? '';
    if (this.price) {
      const price: Price = { ...this.price, amount, stripeID, province: value.province };
      this.run(this.catalog.updatePrice(price, this.product, this.service, this.company), 'Prezzo aggiornato');
    } else {
      const body = { amount, stripeID, provinces: this.selectedProvinces };
      const message = this.selectedProvinces.length === 1 ? 'Prezzo creato' : `${this.selectedProvinces.length} prezzi creati`;
      this.run(this.catalog.createPrices(body, this.product, this.service, this.company), message);
    }
  }

  deletePrice() {
    if (!this.price) return;
    if (!this.confirmDelete) {
      this.confirmDelete = true;
      return;
    }
    this.run(this.catalog.deletePrice(this.price.id), 'Prezzo eliminato', 'Impossibile eliminare il prezzo.');
  }

  private run(request: Observable<unknown>, message: string, fallback = 'Impossibile salvare il prezzo.') {
    this.saving = true;
    request.pipe(takeUntil(this.unsubscribe)).subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit(message);
      },
      error: (error) => {
        this.saving = false;
        this.confirmDelete = false;
        this.errorText = errorMessage(error, fallback);
      },
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
